const MIN_PIXELS_PER_TICK = 6;

const Resolutions = {
  '5m': 5 * 60 * 1000,
  '10m': 10 * 60 * 1000,
  '15m': 15 * 60 * 1000,
  '20m': 20 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '2h': 2 * 60 * 60 * 1000,
  '3h': 3 * 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '12h': 12 * 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000
};

/**
 * @param {number} size - time axes size in pixels
 * @param {number} duration - time in ms
 * @param {number} [minRatio] - min pixel per tick
 * @returns {string}
 */
export function findBestResolution(size, duration, minRatio = MIN_PIXELS_PER_TICK) {
  const resolutions = Object.keys(Resolutions);
  const steps = Object.values(Resolutions);
  const minStep = steps[0];
  const maxStep = steps[steps.length - 1];
  const maxSteps = Math.min(Math.floor(duration / minStep), Math.floor(size / minRatio));
  let bestStep;

  if (duration < minStep) {
    bestStep = minStep;
  } else {
    bestStep = steps.find((step) => duration / step <= maxSteps) ?? maxStep;
  }

  const index = steps.indexOf(bestStep);

  return resolutions[index];
}

export function adjustTimeRange([from, to], resolution) {
  const interval = Resolutions[resolution];
  const now = Date.now();
  const offset = new Date(now).getTimezoneOffset() * 60 * 1000;
  let fromTime = new Date(from).getTime();
  let toTime = new Date(to).getTime();

  fromTime = Math.floor((fromTime - offset) / interval) * interval + offset;
  toTime = Math.ceil((toTime - offset) / interval) * interval + offset - 1;

  while (toTime > now) {
    toTime -= interval;
  }

  return [new Date(fromTime).toISOString(), new Date(toTime).toISOString()];
}

const TOOLTIP_OFFSET_X = 6;
const TOOLTIP_OFFSET_Y = 4;

export function tooltipCursorPositioner(items, event) {
  const { chart, _size: size } = this;
  const { chartArea } = chart;
  const { x, y } = event;
  const xAlign = getXAlign(event, chartArea, size);
  const yAlign = getYAlign(event, chartArea, size);

  return adjustPosition({
    x,
    y,
    xAlign,
    yAlign
  });
}

function getYAlign(position, area, size) {
  const { y } = position;
  const { top, bottom } = area;

  if (size) {
    const { height } = size;

    if (y + height + TOOLTIP_OFFSET_Y * 2 < bottom) {
      return 'top';
    }

    if (y - height - TOOLTIP_OFFSET_Y * 2 > top) {
      return 'bottom';
    }
  }

  const middle = (bottom - top) / 2;

  return y > middle ? 'bottom' : 'top';
}

function getXAlign(position, area, size) {
  const { x } = position;
  const { left, right } = area;

  if (size) {
    const { width } = size;

    if (x + width + TOOLTIP_OFFSET_X * 2 < right) {
      return 'left';
    }

    if (x - width + TOOLTIP_OFFSET_X * 2 > left) {
      return 'right';
    }
  }

  const middle = (right - left) / 2;

  return x > middle ? 'right' : 'left';
}

function adjustPosition(position) {
  const { xAlign, yAlign } = position;
  let { x, y } = position;

  if (xAlign === 'left') {
    x += TOOLTIP_OFFSET_X;
  } else {
    x -= TOOLTIP_OFFSET_X;
  }

  if (yAlign === 'top') {
    y += TOOLTIP_OFFSET_Y;
  } else {
    y -= TOOLTIP_OFFSET_Y;
  }

  return {
    x,
    y,
    xAlign,
    yAlign
  };
}
